import React, { useState, useEffect, useRef } from 'react';
import LifeAssistantService from './services/LifeAssistantService';
import WorkoutTab from './WorkoutTab';
import StatusTab from './StatusTab';
import ActivitiesTab from './ActivitiesTab';
import PreferencesTab from './PreferencesTab';
import HealthHourTab from './HealthHourTab';
import SleepTab from './SleepTab';
import EcgTab from './EcgTab';
import BodyCompositionTab from './BodyCompositionTab';
import StudyPlanTab from './StudyPlanTab';
import NutritionTab from './NutritionTab';
import ExamsTab from './ExamsTab';
import RecoveryCard from './RecoveryCard';
import InfoModal, { InfoButton } from './InfoModal';
import AdminTab from './AdminTab';
import AITab from './AITab';
import NewsTab from './NewsTab';
import AgendaTab from './AgendaTab';
import './NutritionTab.css';
import { useAuth } from './AuthGate';
import { HomeIcon, DumbbellIcon, MailIcon, NotebookIcon, MonitorIcon, TasksIcon, HeartIcon, BookIcon, UtensilsIcon, UsersIcon, LogoutIcon, FlaskIcon, SettingsIcon, MoonIcon, PulseIcon, ScaleIcon, SparkleIcon, InstagramGlyph, NewsIcon, CalendarIcon } from './NavIcons';
import logoLife from './logo_life.png';
import './App.css';

// ===== Refeições (Home): cruza o nome do cardápio de IA com o meal_type
// do diário alimentar, pra mostrar o que foi realmente registrado dentro
// do mesmo card, classificado por refeição. =====
const MEAL_LABEL_TO_TYPE = {
  'café da manhã': 'cafe_manha',
  'lanche da manhã': 'lanche_manha',
  'almoço': 'almoco',
  'lanche da tarde': 'lanche_tarde',
  'jantar': 'jantar',
  'ceia': 'ceia',
};

// Ordem/rótulo pra mostrar refeições registradas no diário que não têm
// (mais) uma entrada correspondente no cardápio de IA — ex.: cardápio
// vazio/apagado, mas a pessoa continua registrando o que comeu.
const MEAL_TYPE_ORDER = [
  ['cafe_manha', 'Café da manhã'],
  ['lanche_manha', 'Lanche da manhã'],
  ['almoco', 'Almoço'],
  ['lanche_tarde', 'Lanche da tarde'],
  ['jantar', 'Jantar'],
  ['ceia', 'Ceia'],
  ['outro', 'Outro'],
];

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// ===== Composição corporal: catálogo de indicadores =====
// As faixas abaixo são de referência usual para adulto — as mesmas classes que
// a balança mostra no app dela. Não são diagnóstico; servem só de orientação.
const bcRange = (v, faixas) => {
  for (const [limite, texto, classe] of faixas) if (v < limite) return [texto, classe];
  return null;
};

const BC_INDICATORS = [
  { key: 'imc', icon: '📐', label: 'IMC', unit: '',
    status: (v) => bcRange(v, [[18.5, 'Baixo', 'low'], [25, 'Saudável', 'good'], [30, 'Alto', 'warn'], [Infinity, 'Obeso', 'bad']]) },
  { key: 'fat_pct', icon: '🥓', label: 'Gordura', unit: '%',
    status: (v) => bcRange(v, [[11, 'Baixo', 'low'], [22, 'Saudável', 'good'], [28, 'Alto', 'warn'], [Infinity, 'Muito alto', 'bad']]) },
  { key: 'fat_kg', icon: '🧈', label: 'Peso da gordura', unit: 'kg' },
  { key: 'skeletal_muscle_pct', icon: '💪', label: 'Massa muscular esquelética', unit: '%',
    status: (v) => bcRange(v, [[33, 'Baixo', 'low'], [40, 'Saudável', 'good'], [Infinity, 'Excelente', 'top']]) },
  { key: 'muscle_score_pct', icon: '🏋️', label: 'Registro de massa muscular', unit: '%',
    status: (v) => bcRange(v, [[69, 'Baixo', 'low'], [Infinity, 'Saudável', 'good']]) },
  { key: 'muscle_kg', icon: '🦾', label: 'Peso da massa muscular', unit: 'kg' },
  { key: 'visceral_fat', icon: '🫁', label: 'Gordura visceral', unit: '',
    status: (v) => bcRange(v, [[10, 'Saudável', 'good'], [15, 'Alto', 'warn'], [Infinity, 'Muito alto', 'bad']]) },
  { key: 'water_pct', icon: '💧', label: 'Água', unit: '%',
    status: (v) => bcRange(v, [[50, 'Baixo', 'low'], [66, 'Saudável', 'good'], [Infinity, 'Alto', 'warn']]) },
  { key: 'water_kg', icon: '🚰', label: 'Peso da água', unit: 'kg' },
  { key: 'metabolism_kcal', icon: '🔥', label: 'Metabolismo', unit: 'kcal/dia' },
  { key: 'obesity_pct', icon: '📊', label: 'Obesidade', unit: '%',
    status: (v) => bcRange(v, [[10, 'Saudável', 'good'], [20, 'Suavemente', 'warn'], [Infinity, 'Alto', 'bad']]) },
  { key: 'bone_kg', icon: '🦴', label: 'Ossos', unit: 'kg' },
  { key: 'protein_pct', icon: '🥚', label: 'Proteína', unit: '%',
    status: (v) => bcRange(v, [[16, 'Baixo', 'low'], [20, 'Saudável', 'good'], [Infinity, 'Alto', 'warn']]) },
  { key: 'lbm_kg', icon: '🧬', label: 'LBM (massa magra)', unit: 'kg' },
  { key: 'metabolic_age', icon: '⏳', label: 'Idade metabólica', unit: 'anos' },
];

// O peso fica no destaque do card, mas também abre histórico ao ser clicado.
const BC_WEIGHT = {
  key: 'weight_kg', icon: '⚖️', label: 'Peso', unit: 'kg',
  status: (v, rec) => (rec?.imc != null ? BC_INDICATORS[0].status(rec.imc) : null),
};

// Faixas de peso (kg) pela altura — a altura sai de peso/IMC, que a balança já dá.
const bcWeightScale = (rec) => {
  if (!rec?.weight_kg || !rec?.imc) return null;
  const h2 = rec.weight_kg / rec.imc;
  return { low: 18.5 * h2, mid: 25 * h2, high: 30 * h2 };
};

function App() {
  const { user: authUser, logout } = useAuth();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState(null);
  const [stats, setStats] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [falando, setFalando] = useState(false);
  const EV_FORM_VAZIO = { title: '', date: todayISO(), allDay: false, start: '09:00', end: '10:00', details: '' };
  const [showEventForm, setShowEventForm] = useState(false);
  const [evForm, setEvForm] = useState(EV_FORM_VAZIO);
  // Ref (não state) porque a corrente de setTimeout entre frases precisa
  // checar o valor na hora, sem esperar um novo render — senão clicar em
  // "parar" no meio não impede a próxima frase já agendada de tocar.
  const vozAtivaRef = useRef(false);
  const [igExpanded, setIgExpanded] = useState(false);
  const [igPostModal, setIgPostModal] = useState(null);
  const [showCargaWatchInfo, setShowCargaWatchInfo] = useState(false);
  const [emails, setEmails] = useState(null);
  const [profile, setProfile] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [foodLog, setFoodLog] = useState([]);
  const [openMeals, setOpenMeals] = useState({});
  const [showMealsMetaInfo, setShowMealsMetaInfo] = useState(false);
  const [activities, setActivities] = useState(null);
  const [bibleVerse, setBibleVerse] = useState(null);
  const [studyToday, setStudyToday] = useState(null);
  const [bodyComposition, setBodyComposition] = useState(null);
  const [bcRowsOpen, setBcRowsOpen] = useState(false);
  const [healthMetrics, setHealthMetrics] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [instagram, setInstagram] = useState(null);
  const [bcModal, setBcModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEmails, setExpandedEmails] = useState({});
  const [collapsedAccounts, setCollapsedAccounts] = useState({});

  const toggleEmail = (key) => {
    setExpandedEmails((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAccount = (key) => {
    setCollapsedAccounts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ===== Navegação (menu hamburger) =====
  const [activeView, setActiveView] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [notebooks, setNotebooks] = useState(null);
  const [allSections, setAllSections] = useState(null);
  const [oneNav, setOneNav] = useState({ notebook: null, section: null, page: null });
  const [sections, setSections] = useState(null);
  const [pages, setPages] = useState(null);
  const [pageHtml, setPageHtml] = useState(null);
  const [oneLoading, setOneLoading] = useState(false);
  const [oneError, setOneError] = useState(null);
  const [noteModal, setNoteModal] = useState(null);

  const svc = LifeAssistantService;

  const openOneNoteTab = () => {
    setActiveView('onenote');
    if (!notebooks) loadNotebooks();
  };

  // Itens do menu — adicionar novas abas aqui.
  const NAV_ITEMS = [
    { key: 'dashboard', label: 'Home', icon: <HomeIcon /> },
    { key: 'ia', label: 'IA', icon: <SparkleIcon /> },
    { key: 'emailfilters', label: 'Configurações', icon: <SettingsIcon /> },
    { key: 'workout', label: 'Treino (Academia)', icon: <DumbbellIcon /> },
    { key: 'agenda', label: 'Agenda', icon: <CalendarIcon /> },
    { key: 'news', label: 'Notícias', icon: <NewsIcon /> },
    { key: 'nutrition', label: 'Refeição', icon: <UtensilsIcon /> },
    { key: 'saude', label: 'Saúde', icon: <HeartIcon /> },
    { key: 'sono', label: 'Sono', icon: <MoonIcon /> },
    { key: 'ecg', label: 'ECG', icon: <PulseIcon /> },
    { key: 'composicao', label: 'Composição Corporal', icon: <ScaleIcon /> },
    { key: 'exames', label: 'Exames', icon: <FlaskIcon /> },
    { key: 'activities', label: 'Atividades (To Do)', icon: <TasksIcon /> },
    { key: 'estudo', label: 'Plano de Estudo', icon: <BookIcon /> },
    { key: 'onenote', label: 'OneNote', icon: <NotebookIcon /> },
    ...(authUser?.role === 'admin' ? [
      { key: 'status', label: 'Status dos Sistemas', icon: <MonitorIcon /> },
      { key: 'admin', label: 'Usuários (admin)', icon: <UsersIcon /> },
    ] : []),
  ];

  // Atalhos de ícone logo abaixo do cabeçalho — mesmos ícones/keys do menu
  // hamburger, só um subconjunto curto pros itens mais usados no dia a dia.
  const QUICK_NAV_ITEMS = [
    { key: 'dashboard', label: 'Home', icon: <HomeIcon /> },
    { key: 'ia', label: 'IA', icon: <SparkleIcon /> },
    { key: 'workout', label: 'Treino', icon: <DumbbellIcon /> },
    { key: 'agenda', label: 'Agenda', icon: <CalendarIcon /> },
    { key: 'news', label: 'Notícias', icon: <NewsIcon /> },
    { key: 'nutrition', label: 'Refeição', icon: <UtensilsIcon /> },
    { key: 'saude', label: 'Saúde', icon: <HeartIcon /> },
    { key: 'sono', label: 'Sono', icon: <MoonIcon /> },
    { key: 'ecg', label: 'ECG', icon: <PulseIcon /> },
    { key: 'composicao', label: 'Composição', icon: <ScaleIcon /> },
    { key: 'exames', label: 'Exames', icon: <FlaskIcon /> },
    { key: 'activities', label: 'To do', icon: <TasksIcon /> },
    { key: 'estudo', label: 'Estudo', icon: <BookIcon /> },
    { key: 'onenote', label: 'OneNote', icon: <NotebookIcon /> },
  ];

  const goToView = (key) => {
    setMenuOpen(false);
    if (key === 'onenote') openOneNoteTab();
    else setActiveView(key);
  };

  const loadNotebooks = async () => {
    setOneLoading(true); setOneError(null);
    try {
      const [nb, sec] = await Promise.all([svc.getOnenoteNotebooks(), svc.getOnenoteSections()]);
      if (nb.error) throw new Error(nb.error);
      setNotebooks(nb.notebooks || []);
      setAllSections(sec.sections || []);
    } catch (e) { setOneError(e.message); } finally { setOneLoading(false); }
  };

  const openNotebook = async (nb) => {
    setOneNav({ notebook: nb, section: null, page: null });
    setSections(null); setPages(null); setPageHtml(null);
    setOneLoading(true); setOneError(null);
    try {
      const d = await svc.getOnenoteSections(nb.id);
      if (d.error) throw new Error(d.error);
      setSections(d.sections || []);
    } catch (e) { setOneError(e.message); } finally { setOneLoading(false); }
  };

  const openSection = async (s) => {
    setOneNav((p) => ({ ...p, section: s, page: null }));
    setPages(null); setPageHtml(null);
    setOneLoading(true); setOneError(null);
    try {
      const d = await svc.getOnenotePages(s.id);
      if (d.error) throw new Error(d.error);
      setPages(d.pages || []);
    } catch (e) { setOneError(e.message); } finally { setOneLoading(false); }
  };

  // O HTML que o Graph devolve traz: (1) cor/fundo fixos (fundo branco) embutidos
  // em estilo inline, e (2) cada bloco de texto com position:absolute/top/left
  // (canvas livre do OneNote) — isso faz o texto "vazar" pra fora do card,
  // porque um container com filhos só absolutos fica com altura zero. Pra virar
  // uma leitura normal (de cima pra baixo) com o tema escuro do app, zera os dois.
  const sanitizeOneNoteHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.body.querySelectorAll('[style]').forEach((el) => {
        el.style.removeProperty('color');
        el.style.removeProperty('background');
        el.style.removeProperty('background-color');
        el.style.removeProperty('position');
        el.style.removeProperty('top');
        el.style.removeProperty('left');
        el.style.removeProperty('width');
        el.style.removeProperty('height');
      });
      doc.body.querySelectorAll('[color]').forEach((el) => el.removeAttribute('color'));
      doc.body.querySelectorAll('[bgcolor]').forEach((el) => el.removeAttribute('bgcolor'));
      return doc.body.innerHTML;
    } catch (e) {
      return html;
    }
  };

  const openPage = async (pg) => {
    setOneNav((p) => ({ ...p, page: pg }));
    setPageHtml(null);
    setOneLoading(true); setOneError(null);
    try {
      const d = await svc.getOnenotePageContent(pg.id);
      if (d.error) throw new Error(d.error);
      setPageHtml(sanitizeOneNoteHtml(d.html || ''));
    } catch (e) { setOneError(e.message); } finally { setOneLoading(false); }
  };

  const oneBack = () => {
    if (oneNav.page) setOneNav((p) => ({ ...p, page: null }));
    else if (oneNav.section) setOneNav((p) => ({ ...p, section: null }));
    else setOneNav({ notebook: null, section: null, page: null });
  };

  const openNoteModal = () => {
    setNoteModal({
      mode: 'existing',
      sectionId: oneNav.section?.id || '',
      notebookId: oneNav.notebook?.id || (notebooks?.[0]?.id || ''),
      newSection: '',
      title: '',
      content: '',
      saving: false,
    });
  };

  const saveNote = async () => {
    const m = noteModal;
    setNoteModal((prev) => ({ ...prev, saving: true }));
    try {
      let sectionId = m.sectionId;
      if (m.mode === 'new') {
        if (!m.newSection.trim()) throw new Error('Dê um nome pra nova seção');
        const s = await svc.createOnenoteSection(m.notebookId, m.newSection.trim());
        if (s.error) throw new Error(s.error);
        sectionId = s.id;
      }
      if (!sectionId) throw new Error('Escolha uma seção');
      if (!m.content.trim() && !m.title.trim()) throw new Error('Escreva algo na nota');
      const r = await svc.createOnenotePage(sectionId, m.title.trim() || 'Nota', m.content);
      if (r.error) throw new Error(r.error);
      setNoteModal(null);
      // recarrega listas
      await loadNotebooks();
      setOneNav({ notebook: null, section: null, page: null });
    } catch (e) {
      alert('Erro ao salvar: ' + e.message);
      setNoteModal((prev) => ({ ...prev, saving: false }));
    }
  };


  // Iniciais pro avatar do cabeçalho: letra do primeiro nome + do último
  // sobrenome (ignora nomes do meio). Com nome só de uma palavra, usa só ela.
  const getInitials = () => {
    const name = (profile?.full_name || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const initials = parts.length > 1
        ? parts[0][0] + parts[parts.length - 1][0]
        : parts[0][0];
      return initials.toUpperCase();
    }
    const email = authUser?.email || '';
    return email ? email[0].toUpperCase() : '?';
  };

  const getFormattedDate = () => {
    const date = new Date();
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

    const dayName = days[date.getDay()].substring(0, 3);
    const dayNum = date.getDate();
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();

    return `${dayName}. ${dayNum} de ${monthName} de ${year}`;
  };

  const getWeatherIcon = (iconCode) => {
    const iconMap = {
      '01d': '☀️', '01n': '🌙',
      '02d': '⛅', '02n': '☁️',
      '03d': '☁️', '03n': '☁️',
      '04d': '☁️', '04n': '☁️',
      '09d': '🌧️', '09n': '🌧️',
      '10d': '🌦️', '10n': '🌧️',
      '11d': '⛈️', '11n': '⛈️',
      '13d': '❄️', '13n': '❄️',
      '50d': '🌫️', '50n': '🌫️',
    };
    return iconMap[iconCode] || '🌤️';
  };

  const getMetricDisplay = (metricName) => {
    const metricMap = {
      'step_count': { name: 'Passos', icon: '👣' },
      'walking_running_distance': { name: 'Distância', icon: '🚶' },
      'apple_exercise_time': { name: 'Tempo Exercício', icon: '⏱️' },
      'apple_stand_time': { name: 'Tempo em Pé', icon: '🧍' },
      'sleep_analysis': { name: 'Sono', icon: '😴' },
      'flights_climbed': { name: 'Andares', icon: '⬆️' },
      'walking_speed': { name: 'Velocidade', icon: '🏃' },
      'blood_oxygen_saturation': { name: 'O2 Sangue', icon: '💨' },
      'apple_stand_hour': { name: 'Horas em Pé', icon: '🕐' },
      'heart_rate': { name: 'Freq. Cardíaca', icon: '❤️' },
      'resting_heart_rate': { name: 'FC Repouso', icon: '💤' },
      'active_energy': { name: 'Energia Ativa', icon: '⚡' },
      'basal_energy_burned': { name: 'Energia Repouso', icon: '🔥' },
      'heart_rate_variability': { name: 'Variabilidade FC', icon: '📊' },
      'walking_heart_rate_average': { name: 'FC Caminhada', icon: '🚶❤️' },
      'environmental_audio_exposure': { name: 'Áudio Ambiental', icon: '🔊' },
      'headphone_audio_exposure': { name: 'Áudio Fones', icon: '🎧' },
      'physical_effort': { name: 'Esforço Físico', icon: '💪' },
      'six_minute_walking_test_distance': { name: 'Teste Caminhada', icon: '🚶📏' },
      'stair_speed_up': { name: 'Velocidade Subida', icon: '⬆️💨' },
      'stair_speed_down': { name: 'Velocidade Descida', icon: '⬇️💨' },
      'time_in_daylight': { name: 'Luz Solar', icon: '☀️' },
      'walking_asymmetry_percentage': { name: 'Assimetria Marcha', icon: '🔄' },
      'walking_double_support_percentage': { name: 'Apoio Duplo', icon: '🦶' },
      'walking_step_length': { name: 'Comprimento Passo', icon: '📏' },
    };
    return metricMap[metricName] || { name: metricName, icon: '📈' };
  };

  const getTrends = () => {
    if (!health?.metrics) return [];

    // Data local (não UTC) — senão à noite no Brasil vira o dia seguinte e nada casa
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const trendMap = {
      'apple_stand_hour': { name: 'Em Pé', unit: 'h', color: '#22d3ee', aggregate: 'sum', decimals: 0 },
      'apple_exercise_time': { name: 'Exercício', unit: 'min', color: '#a3e635', aggregate: 'sum', decimals: 0 },
      'active_energy': { name: 'Movimento', unit: 'cal', color: '#f43f5e', aggregate: 'sum', decimals: 0, factor: 1 / 4.184 },
      'step_count': { name: 'Passos', unit: '', color: '#8b5cf6', aggregate: 'sum', decimals: 0 },
      'walking_running_distance': { name: 'Distância', unit: 'km', color: '#38bdf8', aggregate: 'sum', decimals: 1 },
      'heart_rate': { name: 'Freq. Cardíaca', unit: 'bpm', color: '#f97316', aggregate: 'avg', decimals: 0 },
    };

    const trends = [];

    for (const [key, config] of Object.entries(trendMap)) {
      const metricsByType = health.metrics.filter(m => m.metric_name === key && m.metric_date?.trim() === today);

      if (metricsByType.length === 0) continue;

      let values = [];
      metricsByType.forEach(metric => {
        let dataObj = {};
        try {
          dataObj = JSON.parse(metric.data_json);
        } catch (e) {}

        const value = dataObj.Avg || dataObj.qty || dataObj.value || dataObj.Min || 0;
        if (value) values.push(parseFloat(value));
      });

      if (values.length === 0) continue;

      let raw;
      if (config.aggregate === 'sum') {
        raw = values.reduce((a, b) => a + b, 0);
      } else {
        raw = values.reduce((a, b) => a + b, 0) / values.length;
      }

      if (config.factor) raw = raw * config.factor;

      // Milhar com ponto (pt-BR): 12190 -> "12.190"
      const displayValue = Number(raw.toFixed(config.decimals)).toLocaleString('pt-BR');

      trends.push({
        key,
        name: config.name,
        value: displayValue,
        unit: config.unit,
        color: config.color,
      });
    }

    return trends.slice(0, 6);
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [todayData, healthData, eventsData, statsData, weatherData, emailsData, activitiesData, bibleData, studyTodayData, bodyCompData, healthMetricsData, recoveryData, profileData, nutritionData, instagramData, foodLogData] = await Promise.all([
        LifeAssistantService.getTodaySummary(),
        LifeAssistantService.getHealthData(7),
        LifeAssistantService.getCalendarEvents(30),
        LifeAssistantService.getStats(),
        LifeAssistantService.getWeather(),
        LifeAssistantService.getEmails(),
        LifeAssistantService.getActivities(),
        LifeAssistantService.getBibleVerse().catch(() => null),
        LifeAssistantService.getStudyPlansToday().catch(() => null),
        LifeAssistantService.getBodyComposition(60).catch(() => null),
        LifeAssistantService.getHealthMetrics().catch(() => null),
        LifeAssistantService.getRecovery().catch(() => null),
        LifeAssistantService.getProfile().catch(() => null),
        LifeAssistantService.getNutritionPlan().catch(() => null),
        LifeAssistantService.getInstagramInsights().catch(() => null),
        LifeAssistantService.getFoodLog().catch(() => null),
      ]);
      setData(todayData);
      setHealth(healthData);
      setEvents(eventsData);
      setStats(statsData);
      setWeather(weatherData);
      setEmails(emailsData);
      setActivities(activitiesData);
      setBibleVerse(bibleData);
      setStudyToday(studyTodayData);
      setBodyComposition(bodyCompData?.records || null);
      setHealthMetrics(healthMetricsData);
      setRecovery(recoveryData);
      setProfile(profileData);
      setNutritionPlan(nutritionData?.plan || null);
      setInstagram(instagramData);
      setFoodLog(foodLogData?.entries || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };


  const refreshNutricao = async () => {
    try {
      const [nutritionData, foodLogData] = await Promise.all([
        LifeAssistantService.getNutritionPlan().catch(() => null),
        LifeAssistantService.getFoodLog().catch(() => null),
      ]);
      setNutritionPlan(nutritionData?.plan || null);
      setFoodLog(foodLogData?.entries || []);
    } catch (e) { /* silêncio */ }
  };

  const refreshActivities = async () => {
    try {
      const d = await LifeAssistantService.getActivities();
      setActivities(d);
    } catch (e) { /* silêncio */ }
  };

  const removeCalendarEvent = async (id) => {
    // Remoção só client-side ao vivo (some da tela na hora); se o evento vier
    // de um calendário sincronizado, ele pode voltar no próximo sync — o
    // sync hoje substitui o snapshot inteiro da fonte, não é incremental.
    setEvents((prev) => (prev ? { ...prev, events: prev.events.filter((e) => e.id !== id) } : prev));
    try { await LifeAssistantService.deleteCalendarEvent(id); } catch (e) { /* silêncio */ }
  };

  // Extrai data/hora local diretamente do texto (evita ambiguidade de fuso do
  // Date() do navegador com os formatos "YYYY-MM-DD HH:MM:SS -0300" e
  // "YYYY-MM-DDTHH:MM:SS-03:00" que vêm do backend — ambos já em hora local).
  const fmtDateTime = (raw) => {
    if (!raw) return '';
    const m = /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw);
    if (!m) return '';
    const [, , mo, d, h, mi] = m;
    return `${d}/${mo} ${h}:${mi}`;
  };

  const fmtHMS = (minutes) => {
    const s = Math.max(0, Math.round((minutes || 0) * 60));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  };

  const fmtMeasuredAt = (iso) => {
    if (!iso) return '';
    const [d, t] = iso.split('T');
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y} ${t || ''}`.trim();
  };

  // semUnidade=true devolve só 6'35" (a unidade "/KM" vira sufixo do bloco).
  const fmtPace = (minPerKm, semUnidade = false) => {
    if (!minPerKm) return null;
    const totalSec = Math.round(minPerKm * 60);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const base = `${m}'${String(s).padStart(2, '0')}"`;
    return semUnidade ? base : `${base}/km`;
  };

  const fmtNum = (v, casas = 0) =>
    Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

  // Último ponto de uma série diária do Instagram (reach/profile_views).
  const igLast = (serie) => (serie && serie.length ? serie[serie.length - 1].value : null);

  // O timestamp da mídia do Instagram vem com offset explícito (+0000) —
  // new Date() já converte certo pro fuso do navegador, sem o parsing manual
  // que o fmtDateTime acima usa pros horários já-locais do backend.
  const igFmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
  };

  // Comparativo mês atual (últimos 30 dias) vs os 30 dias anteriores.
  const igMonthDelta = (atual, anterior) => {
    if (anterior == null || atual == null) return null;
    if (anterior === 0) return atual === 0 ? null : `${fmtNum(atual)} — sem dado do mês anterior ainda`;
    const diff = atual - anterior;
    const pct = Math.round((diff / anterior) * 100);
    const sinal = diff > 0 ? '+' : '';
    return `${sinal}${fmtNum(diff)} (${sinal}${pct}%) vs. mês anterior`;
  };

  // Seguidores não têm histórico retroativo na API — só dá comparar com o
  // valor que já estava salvo da última vez que o card foi carregado.
  const igFollowersDelta = (atual, anterior, atualizadoEm) => {
    if (anterior == null || atual == null) return null;
    const diff = atual - anterior;
    if (diff === 0) return `sem mudança desde ${igFmtDateTime(atualizadoEm)}`;
    const sinal = diff > 0 ? '+' : '';
    return `${sinal}${diff} desde ${igFmtDateTime(atualizadoEm)}`;
  };

  // Bloco de estatística no estilo do resumo do Apple Watch: rótulo pequeno
  // em cima, valor grande colorido embaixo.
  const renderWStat = (label, value, tone, unit) => (
    <div className="wstat" key={label}>
      <span className="wstat-label">{label}</span>
      <span className={`wstat-value ${tone}`}>
        {value}{unit && <small>{unit}</small>}
      </span>
    </div>
  );

  // Total geral do mês: soma de TODAS as atividades do relógio/pulseira +
  // o tempo de academia (nosso cronômetro, gym_month_seconds) — sem
  // separar por tipo. A FC média é ponderada pela duração — 40min de
  // corrida pesam mais que 13min.
  const renderWorkoutsGrandTotal = (lista, gymSeconds, gymCalorias, gymSessoes) => {
    if (!lista?.length && !gymSessoes) return null;
    const t = (lista || []).reduce((a, w) => {
      const dur = w.duration_min || 0;
      a.min += dur;
      a.kcal += w.energy_kcal || 0;
      a.kcalAtiva += w.active_energy_kcal || 0;
      a.km += w.distance_km || 0;
      if (w.avg_heart_rate) { a.hrPeso += w.avg_heart_rate * dur; a.hrMin += dur; }
      return a;
    }, { min: 0, kcal: 0, kcalAtiva: 0, km: 0, hrPeso: 0, hrMin: 0 });
    t.min += (gymSeconds || 0) / 60;
    t.kcal += gymCalorias || 0;
    const sessoes = (lista?.length || 0) + (gymSessoes || 0);
    return (
      <>
        <div className="wstat-block wstat-geral">
          <div className="wstat-grid">
            {renderWStat('Tempo Total', fmtHMS(t.min), 'time')}
            {sessoes > 0 && renderWStat('Sessões', sessoes, 'time')}
            {t.km > 0 && renderWStat('Distância', t.km.toFixed(2), 'dist', 'KM')}
            {t.kcalAtiva > 0 && renderWStat('Calorias Ativas', Math.round(t.kcalAtiva), 'cal', 'CAL')}
            {t.kcal > 0 && renderWStat('Total de Calorias', Math.round(t.kcal), 'cal', 'CAL')}
            {t.hrMin > 0 && renderWStat('Batimentos Médios', Math.floor(t.hrPeso / t.hrMin), 'hr', 'bpm')}
          </div>
        </div>
        {showCargaWatchInfo && (
          <InfoModal title="Como classifico Leve / Moderado / Pesado" onClose={() => setShowCargaWatchInfo(false)}>
            <p>
              Cada atividade do Apple Watch (corrida, caminhada etc.) é comparada com o
              <b> seu próprio histórico daquele mesmo tipo de atividade</b>. Corrida não é
              comparada com caminhada, porque a intensidade de FC é bem diferente entre elas.
            </p>
            <p>
              Combino 2 fatores num z score (quantos desvios padrão a sessão está da sua
              média): <b>duração</b> e <b>frequência cardíaca média</b>. A média dos dois
              z scores decide a faixa:
            </p>
            <p>
              <b>Pesado</b>: acima da sua média · <b>Moderado</b>: perto da sua média ·
              {' '}<b>Leve</b>: abaixo da sua média.
            </p>
            <p>
              Só classifico quando há <b>pelo menos 5 sessões anteriores</b> daquele mesmo
              tipo de atividade. Com menos histórico que isso, a média/desvio padrão não
              são confiáveis, então a sessão fica sem selo (é o caso, por exemplo, de uma
              modalidade nova que você ainda fez poucas vezes).
            </p>
            <p>Não usa nenhum modelo de IA. É cálculo estatístico direto sobre seus dados.</p>
          </InfoModal>
        )}
      </>
    );
  };

  const acToday = activities?.today;
  const todayActs = (activities?.activities || []).filter(
    (a) => a.due_date === acToday || (!a.done && a.due_date < acToday)
  );

  const toggleActFromCard = async (id) => {
    try { await LifeAssistantService.toggleActivity(id); refreshActivities(); }
    catch (e) { /* silêncio */ }
  };

  // Assistente de voz — Web Speech API nativa do navegador, sem custo e sem
  // IA. Cada tópico (clima, aniversário, agenda, atividades) vira uma frase
  // separada, falada em sequência com uma pausa real entre elas (setTimeout
  // no onend de cada uma) — juntar tudo numa frase só soa corrido demais.
  const falarSequencia = (frases, idx = 0) => {
    if (!vozAtivaRef.current || idx >= frases.length) { setFalando(false); return; }
    const utter = new window.SpeechSynthesisUtterance(frases[idx]);
    utter.lang = 'pt-BR';
    utter.rate = 0.95;
    utter.onend = () => setTimeout(() => falarSequencia(frases, idx + 1), 500);
    utter.onerror = () => { vozAtivaRef.current = false; setFalando(false); };
    window.speechSynthesis.speak(utter);
  };

  // Criar evento: abre o Google Calendar já preenchido (link oficial de
  // template). Não usa a API do Google — escopo de calendário é sensível e o
  // app só tem Gmail autorizado; assim funciona em qualquer conta/celular.
  const abrirNoGoogleCalendar = () => {
    const { title, date, allDay, start, end, details } = evForm;
    if (!title.trim() || !date) return;
    const d = date.replace(/-/g, '');
    const pad = (n) => String(n).padStart(2, '0');
    let dates;
    if (allDay) {
      const prox = new Date(`${date}T12:00:00`);
      prox.setDate(prox.getDate() + 1);
      dates = `${d}/${prox.getFullYear()}${pad(prox.getMonth() + 1)}${pad(prox.getDate())}`;
    } else {
      // Fim antes (ou igual) ao início → assume 1h de duração.
      let fim = end;
      if (!fim || fim <= start) {
        const [h, m] = start.split(':').map(Number);
        fim = `${pad(Math.min(h + 1, 23))}:${pad(h + 1 > 23 ? 59 : m)}`;
      }
      dates = `${d}T${start.replace(':', '')}00/${d}T${fim.replace(':', '')}00`;
    }
    const params = new URLSearchParams({ action: 'TEMPLATE', text: title.trim(), dates, ctz: 'America/Sao_Paulo' });
    if (details.trim()) params.set('details', details.trim());
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener,noreferrer');
    setShowEventForm(false);
    setEvForm(EV_FORM_VAZIO);
  };

  // "há 2 dias" / "ontem" / "hoje" a partir de uma data YYYY-MM-DD.
  const falarDiasAtras = (isoDate) => {
    if (!isoDate) return '';
    const d1 = new Date(`${isoDate}T12:00:00`);
    const d0 = new Date(`${todayISO()}T12:00:00`);
    const dias = Math.round((d0 - d1) / 86400000);
    if (dias <= 0) return 'hoje';
    if (dias === 1) return 'ontem';
    return `há ${dias} dias`;
  };

  const falarResumo = async () => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    if (falando) { vozAtivaRef.current = false; setFalando(false); return; }

    setFalando(true);
    const frases = [];
    const limparTitulo = (t) => (t || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
    const hoje = todayISO();

    if (weather && weather.temp != null) {
      frases.push(`Agora são ${Math.round(weather.temp)} graus em ${weather.city}.`);
      if (weather.temp_min != null && weather.temp_max != null) {
        frases.push(`A temperatura hoje deve variar entre ${Math.round(weather.temp_min)} e ${Math.round(weather.temp_max)} graus.`);
      }
    }

    const todosEventosHoje = (events?.events || []).filter((e) => e.event_date === hoje);
    const aniversariantesHoje = todosEventosHoje.filter(
      (e) => e.title?.toLowerCase().includes('aniversário') || e.title?.toLowerCase().includes('birthday')
    );
    const agendaHoje = todosEventosHoje.filter(
      (e) => !e.title?.toLowerCase().includes('aniversário') && !e.title?.toLowerCase().includes('birthday')
    );

    if (aniversariantesHoje.length > 0) {
      const nomes = aniversariantesHoje.map((e) => limparTitulo(e.title).replace(/aniversário de\s*/i, ''));
      frases.push(`Hoje é aniversário de ${nomes.join(' e ')}.`);
    }

    if (agendaHoje.length > 0) {
      const itens = agendaHoje.map((e) => limparTitulo(e.title));
      frases.push(`Sua agenda de hoje: ${itens.join(', ')}.`);
    } else {
      frases.push('Você não tem compromissos na agenda hoje.');
    }

    const pendentes = todayActs.filter((a) => !a.done).length;
    if (pendentes > 0) {
      frases.push(`Você tem ${pendentes} ${pendentes === 1 ? 'atividade pendente' : 'atividades pendentes'} pra hoje.`);
    }

    const totalEmails = (emails?.groups || []).reduce((sum, g) => sum + (g.emails?.length || 0), 0);
    if (totalEmails > 0) {
      frases.push(`Você tem ${totalEmails} ${totalEmails === 1 ? 'e-mail importante' : 'e-mails importantes'} hoje.`);
    }

    if (recovery?.disponivel) {
      frases.push(`Sua recuperação hoje está em ${recovery.score}%, classificada como ${recovery.nivel}.`);
    }

    // Último treino — busca na hora do clique (não fica pesando o carregamento
    // normal do dashboard, só é usado quando a pessoa pede o resumo falado).
    try {
      const hist = await LifeAssistantService.getWorkoutHistory(1);
      const ultimo = hist?.sessions?.[0];
      if (ultimo) {
        const minutos = Math.round((ultimo.total_seconds || 0) / 60);
        frases.push(
          `Seu último treino foi ${ultimo.nome || 'sem nome'}, ${falarDiasAtras(ultimo.session_date)}, com duração de ${minutos} minutos.`
        );
      }
    } catch (e) { /* silêncio — só não entra essa frase */ }

    if (frases.length === 0) { setFalando(false); return; }
    vozAtivaRef.current = true;
    falarSequencia(frases);
  };

  const renderBodyComp = () => {
    const cur = bodyComposition[0];
    const prev = bodyComposition[1];
    const delta = prev && cur.weight_kg != null && prev.weight_kg != null
      ? cur.weight_kg - prev.weight_kg : null;
    const scale = bcWeightScale(cur);
    const pesoStatus = cur.imc != null ? BC_INDICATORS[0].status(cur.imc) : null;

    let segs = null;
    if (scale) {
      const min = scale.low * 0.85, max = scale.high * 1.08;
      const pct = (v) => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
      segs = [
        { cls: 'low', label: 'Baixo', w: pct(scale.low), edge: scale.low },
        { cls: 'good', label: 'Saudável', w: pct(scale.mid) - pct(scale.low), edge: scale.mid },
        { cls: 'warn', label: 'Alto', w: pct(scale.high) - pct(scale.mid), edge: scale.high },
        { cls: 'bad', label: 'Obeso', w: 100 - pct(scale.high), edge: null },
      ];
      segs.markerLeft = pct(cur.weight_kg);
    }

    return (
      <section
        className="card bodycomp-card"
        onClick={() => setBcRowsOpen((o) => !o)}
        role="button"
        tabIndex={0}
      >
        <h3>🧍 Composição Corporal</h3>

        <div className="bc-hero">
          <div className="bc-hero-top">
            <div>
              <span className="bc-hero-value">{cur.weight_kg}<small>kg</small></span>
              <span className="bc-hero-date">{fmtMeasuredAt(cur.measured_at)}</span>
            </div>
            {pesoStatus && <span className={`bc-badge ${pesoStatus[1]}`}>{pesoStatus[0]}</span>}
          </div>

          {segs && (
            <div className="bc-scale">
              <div className="bc-scale-bar">
                {segs.map((s) => <span key={s.cls} className={`bc-seg ${s.cls}`} style={{ width: `${s.w}%` }} />)}
                <span className="bc-marker" style={{ left: `${segs.markerLeft}%` }} />
              </div>
              <div className="bc-scale-labels">
                {segs.map((s) => (
                  <span key={s.cls} style={{ width: `${s.w}%` }}>
                    {s.label}
                    {s.edge != null && <b>{s.edge.toFixed(1)}</b>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {delta != null && (
            <div className="bc-delta">
              <span>Comparado com a última vez ({fmtMeasuredAt(prev.measured_at).split(' ')[0]})</span>
              <b className={delta > 0 ? 'up' : delta < 0 ? 'down' : ''}>
                {delta > 0 ? '+' : ''}{delta.toFixed(2)} kg
              </b>
            </div>
          )}

          {cur.nutricao_periodo && (
            <div className="bc-delta">
              <span>Média alimentar no período ({cur.nutricao_periodo.dias_registrados} dia(s) registrado(s))</span>
              <b>{cur.nutricao_periodo.media_calorias_dia} kcal/dia</b>
            </div>
          )}
        </div>

        {(() => {
          const disponiveis = [BC_WEIGHT, ...BC_INDICATORS].filter((ind) => cur[ind.key] != null);
          return (
            <>
              {bcRowsOpen && (
                <div className="bc-rows" onClick={(e) => e.stopPropagation()}>
                  {disponiveis.map((ind) => {
                    const v = cur[ind.key];
                    const st = ind.status ? ind.status(v, cur) : null;
                    return (
                      <button className="bc-row" key={ind.key} onClick={() => setBcModal(ind)}>
                        <span className="bc-row-icon">{ind.icon}</span>
                        <span className="bc-row-label">{ind.label}</span>
                        <span className="bc-row-right">
                          <span className="bc-row-value">{v}{ind.unit && <small>{ind.unit}</small>}</span>
                          {st && <span className={`bc-badge ${st[1]}`}>{st[0]}</span>}
                        </span>
                        <span className="bc-row-arrow">›</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
      </section>
    );
  };

  if (loading) return <div className="loading-screen">⏳ Carregando...</div>;

  return (
    <div className="app-mobile">
      <header className="mobile-header">
        <button className="hamburger" onClick={() => setMenuOpen(true)} aria-label="Menu">☰</button>
        <div className="header-content">
          <h1 className="header-title">
            <span className="header-title-text">
              <span className="brand-text">Life Assistant</span>
              {profile?.apelido && <span className="brand-subtitle">{profile.apelido}</span>}
            </span>
          </h1>
        </div>
        <img src={logoLife} className="header-logo" alt="Life Assistant" />
      </header>

      <div className="quick-nav">
        {QUICK_NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            className={`quick-nav-item ${activeView === item.key ? 'active' : ''}`}
            onClick={() => goToView(item.key)}
          >
            <span className="quick-nav-icon">{item.icon}</span>
            <span className="quick-nav-label">{item.label}</span>
          </button>
        ))}
      </div>

      <div className="date-bar" style={{ display: 'none' }}>
        <p className="date-text">
          {getFormattedDate()}
          {activeView !== 'dashboard' && (
            <span className="date-view"> · {NAV_ITEMS.find((i) => i.key === activeView)?.label}</span>
          )}
        </p>
      </div>

      {menuOpen && (
        <div className="drawer-overlay" onClick={() => setMenuOpen(false)}>
          <nav className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <span>Menu</span>
              <button className="drawer-close" onClick={() => setMenuOpen(false)}>✕</button>
            </div>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                className={`drawer-item ${activeView === item.key ? 'active' : ''}`}
                onClick={() => goToView(item.key)}
              >
                <span className="drawer-item-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
            <button className="drawer-item drawer-item-logout" onClick={logout}>
              <span className="drawer-item-icon"><LogoutIcon /></span>
              <span>Sair</span>
            </button>
          </nav>
        </div>
      )}

      <div className="mobile-content">
        {activeView === 'dashboard' && (
        <>
        {error && <div className="error-banner">{error}</div>}

        {/* Assistente de voz — teste: lê clima, aniversariantes, agenda e
            atividades de hoje em voz alta (Web Speech API do navegador). */}
        <button
          className={`voice-assistant-btn ${falando ? 'speaking' : ''}`}
          onClick={falarResumo}
        >
          {falando ? '🔊 Falando… (toque pra parar)' : '🔊 Ouvir resumo do dia'}
        </button>

        {/* Clima Premium */}
        {weather && !weather.error && (
          <section
            className="weather-premium"
            onClick={() => setWeatherExpanded((v) => !v)}
            role="button"
            tabIndex={0}
          >
            <div className="weather-header">
              <h2 className="weather-city">{weather.city} 📍</h2>
              <div className="weather-icon-block">
                <div className="weather-icon">{getWeatherIcon(weather.icon)}</div>
                <div className="weather-desc-top">{weather.description}</div>
              </div>
            </div>

            <div className="weather-row">
              <div className="weather-temp-block">
                <span className="temp-value">{weather.temp}°</span>
                <span className="minmax">Máx {weather.temp_max}° Min {weather.temp_min}°</span>
              </div>
              <div className="weather-humidity">
                <span className="humidity-value">{weather.humidity}%</span>
                <span className="humidity-label">Umidade</span>
              </div>
            </div>

            {weatherExpanded && (
              <div className="weather-forecast" onClick={(e) => e.stopPropagation()}>
                {weather.hourly?.length > 0 && (
                  <div className="weather-hourly">
                    {weather.hourly.map((h, i) => (
                      <div className="weather-hourly-item" key={i}>
                        <span className="weather-hourly-hour">{h.hour}</span>
                        <span className="weather-hourly-icon">{getWeatherIcon(h.icon)}</span>
                        <span className="weather-hourly-temp">{h.temp}°</span>
                      </div>
                    ))}
                  </div>
                )}

                {weather.daily?.length > 0 && (() => {
                  const maxT = Math.max(...weather.daily.map((d) => d.temp_max));
                  const minT = Math.min(...weather.daily.map((d) => d.temp_min));
                  const span = Math.max(1, maxT - minT);
                  return (
                    <div className="weather-daily">
                      {weather.daily.map((d, i) => {
                        const left = ((d.temp_min - minT) / span) * 100;
                        const width = Math.max(8, ((d.temp_max - d.temp_min) / span) * 100);
                        return (
                          <div className="weather-daily-row" key={i}>
                            <span className="weather-daily-weekday">{d.weekday}</span>
                            <span className="weather-daily-icon">{getWeatherIcon(d.icon)}</span>
                            <span className="weather-daily-min">{d.temp_min}°</span>
                            <span className="weather-daily-bar-track">
                              <span
                                className="weather-daily-bar"
                                style={{ left: `${left}%`, width: `${width}%` }}
                              />
                            </span>
                            <span className="weather-daily-max">{d.temp_max}°</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </section>
        )}

        {/* Versículo bíblico do dia */}
        {bibleVerse && bibleVerse.text && (
          <section className="card bible-card">
            <h3>📖 Versículo do dia</h3>
            <p className="bible-text">"{bibleVerse.text}"</p>
            <p className="bible-ref">{bibleVerse.reference}</p>
          </section>
        )}

        {/* Refeições — cardápio de IA (se houver) + o que foi registrado no
            diário alimentar hoje. Aparece mesmo sem cardápio, pra quem só
            registra manualmente ou apagou as sugestões da IA. */}
        {(() => {
          const foodLogHoje = foodLog.filter((e) => e.log_date === todayISO());
          const temPlano = (nutritionPlan?.refeicoes?.length || 0) > 0;
          if (!temPlano && foodLogHoje.length === 0) return null;

          const consumidoHoje = foodLogHoje.reduce((sum, e) => sum + (e.calorias != null ? Number(e.calorias) : 0), 0);
          // Restante pra comer hoje = meta da IA − consumido. A meta já foi
          // calculada pela IA considerando o gasto típico da pessoa (histórico
          // de passos/treinos dos últimos 7 dias) — não soma/subtrai gasto do
          // relógio de novo aqui, senão conta a atividade duas vezes.
          const metaCalorias = nutritionPlan?.meta?.calorias_dia;
          const restante = metaCalorias != null ? Math.round(metaCalorias - consumidoHoje) : null;
          // Verde = sobra folga, amarelo = perto de bater a meta (≤15%
          // restante), vermelho = já passou da meta.
          let corRestante = 'good';
          if (restante != null) {
            if (restante <= 0) corRestante = 'bad';
            else if (metaCalorias && restante / metaCalorias <= 0.15) corRestante = 'warn';
          }

          // Tipos já cobertos por uma refeição do cardápio de IA, pra não
          // duplicar quando também sobrar registro avulso do mesmo tipo.
          const tiposNoPlano = new Set(
            (nutritionPlan?.refeicoes || [])
              .map((r) => MEAL_LABEL_TO_TYPE[(r.nome || '').replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase()])
              .filter(Boolean)
          );
          const extras = MEAL_TYPE_ORDER
            .filter(([key]) => !tiposNoPlano.has(key))
            .map(([key, label]) => ({
              key, label,
              entries: foodLogHoje.filter((e) => e.meal_type === key),
            }))
            .filter((g) => g.entries.length > 0);

          return (
          <section className="card">
            <h3>
              🍽️ Refeições
              {nutritionPlan?.meta && (
                <InfoButton onClick={() => setShowMealsMetaInfo(true)} />
              )}
              {restante != null && (
                <span className={`nt-saldo-cal ${corRestante}`}>
                  {restante}/{metaCalorias} kcal
                </span>
              )}
            </h3>
            {showMealsMetaInfo && nutritionPlan?.meta && (
              <InfoModal title="Metas nutricionais do dia" onClose={() => setShowMealsMetaInfo(false)}>
                {restante != null && (
                  <div className="nt-restante-calc">
                    <p>
                      Meta da IA <b>{metaCalorias} kcal</b> − consumido hoje <b className="good">{Math.round(consumidoHoje)} kcal</b>
                      {' '}= <b className={corRestante}>{restante} kcal</b>{restante >= 0 ? ' pra comer ainda hoje' : ' acima da meta'}.
                    </p>
                    <p className="nt-restante-legenda">
                      <span className="good">●</span> tranquilo &nbsp;
                      <span className="warn">●</span> perto da meta &nbsp;
                      <span className="bad">●</span> passou da meta
                    </p>
                  </div>
                )}
                <div className="nt-meta-card">
                  <div className="nt-meta-row">
                    <div className="nt-meta-stat">
                      <span className="nt-meta-label">Calorias</span>
                      <span className="nt-meta-value">{nutritionPlan.meta.calorias_dia}</span>
                    </div>
                    <div className="nt-meta-stat">
                      <span className="nt-meta-label">Proteína</span>
                      <span className="nt-meta-value">{nutritionPlan.meta.proteina_g}g</span>
                    </div>
                    <div className="nt-meta-stat">
                      <span className="nt-meta-label">Carboidrato</span>
                      <span className="nt-meta-value">{nutritionPlan.meta.carboidrato_g}g</span>
                    </div>
                    <div className="nt-meta-stat">
                      <span className="nt-meta-label">Gordura</span>
                      <span className="nt-meta-value">{nutritionPlan.meta.gordura_g}g</span>
                    </div>
                  </div>
                  {nutritionPlan.meta.racional && <p className="nt-racional">{nutritionPlan.meta.racional}</p>}
                </div>
              </InfoModal>
            )}
            <div className="nt-meals nt-meals-compact">
              {(nutritionPlan?.refeicoes || []).map((r, i) => {
                const aberto = !!openMeals[i];
                // Tira qualquer complemento entre parênteses do nome (ex.:
                // "Lanche da tarde (pós treino)" → "Lanche da tarde").
                const nomeCurto = (r.nome || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
                const mealTypeKey = MEAL_LABEL_TO_TYPE[nomeCurto.toLowerCase()];
                const registradosHoje = mealTypeKey
                  ? foodLogHoje.filter((e) => e.meal_type === mealTypeKey)
                  : [];
                return (
                  <div className="nt-meal-card" key={i}>
                    <button
                      type="button"
                      className="nt-meal-head nt-meal-head-btn"
                      onClick={() => setOpenMeals((o) => ({ ...o, [i]: !o[i] }))}
                      aria-expanded={aberto}
                    >
                      <span className="nt-meal-name">{nomeCurto}</span>
                      {r.calorias != null && <span className="nt-meal-cal-inline">{r.calorias} kcal</span>}
                    </button>
                    {aberto && (
                      <>
                        <ul className="nt-meal-items">
                          {(r.itens || []).map((item, j) => <li key={j}>{item}</li>)}
                        </ul>
                        {registradosHoje.length > 0 && (
                          <div className="nt-meal-registrado">
                            <span className="nt-meal-registrado-label">Registrado hoje</span>
                            <ul className="nt-meal-items">
                              {registradosHoje.map((e) => (
                                <li key={e.id}>{e.description}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {/* Registrado hoje sem (ou fora do) cardápio de IA — mesmo
                  padrão clicável: clica pra ver o que foi cadastrado. */}
              {extras.map((g) => {
                const chave = `extra-${g.key}`;
                const aberto = !!openMeals[chave];
                const totalGrupo = g.entries.reduce((sum, e) => sum + (e.calorias != null ? Number(e.calorias) : 0), 0);
                return (
                  <div className="nt-meal-card" key={chave}>
                    <button
                      type="button"
                      className="nt-meal-head nt-meal-head-btn"
                      onClick={() => setOpenMeals((o) => ({ ...o, [chave]: !o[chave] }))}
                      aria-expanded={aberto}
                    >
                      <span className="nt-meal-name">{g.label}</span>
                      {totalGrupo > 0 && <span className="nt-meal-cal-inline">{Math.round(totalGrupo)} kcal</span>}
                    </button>
                    {aberto && (
                      <ul className="nt-meal-items">
                        {g.entries.map((e) => (
                          <li key={e.id}>{e.description}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          );
        })()}

        {/* Instagram — as 3 últimas publicações ficam sempre visíveis; o
            cabeçalho (logo+foto) é o botão que expande/retrai só as métricas
            (seguidores/alcance/visitas). Grid próprio (não herda de
            .hm-grid) pra não quebrar feio no mobile nem esticar no desktop. */}
        {instagram?.connected && (
          <section className="card ig-card">
            <button
              type="button"
              className="ig-header-btn"
              onClick={() => setIgExpanded((v) => !v)}
              aria-expanded={igExpanded}
            >
              <span className="ig-header-left">
                <InstagramGlyph />
                <span className="ig-header-title">Instagram</span>
              </span>
              <span className="ig-header-right">
                {instagram.profile_picture_url && (
                  <img className="ig-avatar" src={instagram.profile_picture_url} alt="" />
                )}
                <span className="ig-chevron">{igExpanded ? '▲' : '▼'}</span>
              </span>
            </button>

            {instagram.recent_posts?.length > 0 && (
              <div className="ig-posts-row">
                {instagram.recent_posts.map((p) => (
                  <button
                    key={p.id}
                    className="ig-post-thumb-btn"
                    onClick={() => setIgPostModal(p)}
                    aria-label="Ver publicação"
                  >
                    {(p.thumbnail_url || p.media_url) ? (
                      <>
                        <img className="ig-post-thumb" src={p.thumbnail_url || p.media_url} alt="" />
                        <span className="ig-post-overlay">
                          <span className="ig-post-stat">❤️ {fmtNum(p.like_count ?? 0)}</span>
                          <span className="ig-post-stat">💬 {fmtNum(p.comments_count ?? 0)}</span>
                        </span>
                      </>
                    ) : (
                      <span className="ig-post-thumb ig-post-thumb-empty" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {igExpanded && (
              <div className="ig-body">
                <div className="ig-metrics-row">
                  <div className="ig-metric">
                    <span className="ig-metric-label">Seguidores</span>
                    <span className="ig-metric-value">{fmtNum(instagram.followers_count)}</span>
                    {igFollowersDelta(instagram.followers_count, instagram.followers_prev, instagram.followers_prev_at) && (
                      <span className="ig-delta">
                        {igFollowersDelta(instagram.followers_count, instagram.followers_prev, instagram.followers_prev_at)}
                      </span>
                    )}
                  </div>
                  <div className="ig-metric">
                    <span className="ig-metric-label">Alcance (30d)</span>
                    <span className="ig-metric-value">{fmtNum(instagram.reach_month ?? 0)}</span>
                    {igMonthDelta(instagram.reach_month, instagram.reach_month_prev) && (
                      <span className="ig-delta">{igMonthDelta(instagram.reach_month, instagram.reach_month_prev)}</span>
                    )}
                  </div>
                  <div className="ig-metric">
                    <span className="ig-metric-label">Visitas ao perfil (30d)</span>
                    <span className="ig-metric-value">{fmtNum(instagram.profile_views_month ?? 0)}</span>
                    {igMonthDelta(instagram.profile_views_month, instagram.profile_views_month_prev) && (
                      <span className="ig-delta">
                        {igMonthDelta(instagram.profile_views_month, instagram.profile_views_month_prev)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {igPostModal && (
              <InfoModal
                title={igPostModal.timestamp ? igFmtDateTime(igPostModal.timestamp) : 'Publicação'}
                onClose={() => setIgPostModal(null)}
              >
                {(igPostModal.thumbnail_url || igPostModal.media_url) && (
                  <img className="ig-modal-thumb" src={igPostModal.thumbnail_url || igPostModal.media_url} alt="" />
                )}
                <p>❤️ <b>{fmtNum(igPostModal.like_count ?? 0)}</b> curtidas</p>
                <p>💬 <b>{fmtNum(igPostModal.comments_count ?? 0)}</b> comentários</p>
                {igPostModal.reach != null && <p>👁 <b>{fmtNum(igPostModal.reach)}</b> contas alcançadas</p>}
                {igPostModal.saved != null && <p>🔖 <b>{fmtNum(igPostModal.saved)}</b> salvamentos</p>}
                {igPostModal.permalink && (
                  <a className="ig-last-post-link" href={igPostModal.permalink} target="_blank" rel="noreferrer">
                    Ver no Instagram
                  </a>
                )}
              </InfoModal>
            )}
          </section>
        )}

        {/* Aniversários */}
        {/* Só hoje — os próximos dias ficam guardados mas não aparecem na Home. */}
        {(() => {
          const aniversariosHoje = (events?.events || []).filter(
            (e) => e.event_date === todayISO()
              && (e.title?.toLowerCase().includes('aniversário') || e.title?.toLowerCase().includes('birthday'))
          );
          if (aniversariosHoje.length === 0) return null;
          return (
            <section className="card anniversary-card">
              <h3>🎂 Aniversários</h3>
              <div className="events-scroll">
                {aniversariosHoje.map((event, idx) => (
                  <div key={event.id ?? idx} className="anniversary-item">
                    <div className="event-title">{(event.title || '').replace(/^[^\p{L}\p{N}]+/u, '')}</div>
                    {event.id != null && (
                      <button className="ac-del" onClick={() => removeCalendarEvent(event.id)} aria-label="Excluir">✕</button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Agenda/Eventos */}
        {events && (() => {
          const agendaItens = (events.events || []).filter(
            (e) => e.event_date === todayISO()
              && !e.title?.toLowerCase().includes('aniversário') && !e.title?.toLowerCase().includes('birthday')
          );
          return (
            <section className="card agenda-card">
              <div className="analise-head">
                <h3>📅 Agenda</h3>
                <button className="analise-refresh" onClick={() => setShowEventForm((v) => !v)}>
                  {showEventForm ? 'Fechar' : '＋ Evento'}
                </button>
              </div>

              {showEventForm && (
                <div className="ev-form">
                  <input
                    className="ev-input" placeholder="Título do evento"
                    value={evForm.title} onChange={(e) => setEvForm((f) => ({ ...f, title: e.target.value }))}
                  />
                  <div className="ev-row">
                    <input
                      className="ev-input" type="date" value={evForm.date}
                      onChange={(e) => setEvForm((f) => ({ ...f, date: e.target.value }))}
                    />
                    <label className="ev-check">
                      <input
                        type="checkbox" checked={evForm.allDay}
                        onChange={(e) => setEvForm((f) => ({ ...f, allDay: e.target.checked }))}
                      /> Dia inteiro
                    </label>
                  </div>
                  {!evForm.allDay && (
                    <div className="ev-row">
                      <input
                        className="ev-input" type="time" value={evForm.start}
                        onChange={(e) => setEvForm((f) => ({ ...f, start: e.target.value }))}
                      />
                      <span className="ev-sep">até</span>
                      <input
                        className="ev-input" type="time" value={evForm.end}
                        onChange={(e) => setEvForm((f) => ({ ...f, end: e.target.value }))}
                      />
                    </div>
                  )}
                  <input
                    className="ev-input" placeholder="Descrição (opcional)"
                    value={evForm.details} onChange={(e) => setEvForm((f) => ({ ...f, details: e.target.value }))}
                  />
                  <button
                    className="ev-submit" onClick={abrirNoGoogleCalendar}
                    disabled={!evForm.title.trim() || !evForm.date}
                  >
                    Abrir no Google Calendar
                  </button>
                  <p className="ev-hint">Abre o Google Calendar já preenchido — é só tocar em Salvar lá.</p>
                </div>
              )}

              {agendaItens.length > 0 ? (
                <div className="events-scroll">
                  {agendaItens.map((event, idx) => (
                    <div key={idx} className="event-item">
                      <div className="event-title">{(event.title || '').replace(/^[^\p{L}\p{N}]+/u, '')}</div>
                      {event.start_raw && event.start_raw !== '00:00' && (
                        <div className="event-time">{event.start_raw}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !showEventForm && <p className="ev-empty">Nenhum compromisso por enquanto.</p>
              )}
            </section>
          );
        })()}

        {/* Atividades de hoje */}
        {todayActs.length > 0 && (
          <section className="card activities-card">
            <h3>✅ Atividades de hoje</h3>
            <div className="activities-list">
              {todayActs.map((a) => (
                <div className={`activity-item ${a.done ? 'done' : ''}`} key={a.id}>
                  <button
                    className={`activity-check ${a.done ? 'on' : ''}`}
                    onClick={() => toggleActFromCard(a.id)}
                    aria-label="Concluir"
                  >{a.done ? '✓' : ''}</button>
                  <span className="activity-name">{a.name}</span>
                  {!a.done && a.due_date < acToday && <span className="activity-late">atrasada</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Plano de estudo de hoje */}
        {studyToday?.plans?.length > 0 && (
          <section className="card study-card">
            <h3>📚 Plano de estudo de hoje</h3>
            <div className="activities-list">
              {studyToday.plans.map((p) => (
                <div className="activity-item" key={p.id}>
                  <span className="activity-name">
                    {p.name}
                    {p.notes && <span className="workout-datetime">🎯 {p.notes}</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* E-mails */}
        {emails && emails.groups?.length > 0 && (
          <section className="card emails-card">
            <h3>📧 E-mails</h3>
            <div className="emails-list">
              {emails.groups.map((group, gi) => {
                const accountOpen = !collapsedAccounts[gi];
                return (
                  <div key={gi} className="email-group">
                    <button
                      className="email-account-header"
                      onClick={() => toggleAccount(gi)}
                    >
                      <span className="email-account">{group.account}</span>
                      <span className="email-count">{group.emails.length}</span>
                      <span className="account-chevron">{accountOpen ? '▲' : '▼'}</span>
                    </button>
                    {accountOpen && group.emails.map((email, ei) => {
                      const key = `${gi}-${ei}`;
                      const isOpen = !!expandedEmails[key];
                      return (
                        <div key={ei} className={`email-item ${isOpen ? 'open' : ''}`}>
                          <button
                            className="email-header"
                            onClick={() => toggleEmail(key)}
                          >
                            <div className="email-header-text">
                              <div className="email-from">{email.from}</div>
                              <div className="email-subject">{email.subject}</div>
                            </div>
                            <span className="email-chevron">{isOpen ? '▲' : '▼'}</span>
                          </button>
                          {isOpen && email.snippet && (
                            <div className="email-snippet">{email.snippet}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Recuperação (score de bem estar) */}
        <RecoveryCard data={recovery} />

        {/* Saúde e Métricas — um card por indicador, com o gráfico do dia */}
        {healthMetrics?.metrics?.length > 0 && (
          <section className="card health-metrics-card">
            <h3>❤️ Atividades</h3>
            <div className="hm-bars">
              {healthMetrics.metrics.filter((m) => m.key !== 'heart_rate').map((m) => {
                const meta = m.key === 'apple_stand_hour' ? (profile?.meta_em_pe_horas ?? 12)
                  : m.key === 'apple_exercise_time' ? (profile?.meta_exercicio_min ?? 30)
                  : m.key === 'active_energy' ? (profile?.meta_calorias ?? 500)
                  : m.key === 'step_count' ? (profile?.meta_passos ?? 12000)
                  : m.key === 'walking_running_distance' ? (profile?.meta_distancia_km ?? 6)
                  : null;
                const atual = m.today || 0;
                const pct = meta ? Math.min(100, (atual / meta) * 100) : 0;
                return (
                  <div className="hm-bar-row" key={m.key}>
                    <div className="hm-bar-head">
                      <span className="hm-bar-label">{m.label}</span>
                      <span className="hm-bar-goal">
                        {fmtNum(atual, m.decimals)}{meta != null && `/${fmtNum(meta, m.decimals)}`}
                        {m.unit && <small>{m.unit}</small>}
                      </span>
                    </div>
                    <div className="hm-bar-track">
                      <div className="hm-bar-fill" style={{ width: `${pct}%`, background: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Composição corporal (balança) */}
        {bodyComposition?.length > 0 && renderBodyComp()}

        {/* Treinos (Apple Watch / Health + academia) — total do mês, sem drill-down */}
        {health && (health.workouts_month?.length > 0 || health.gym_month_sessoes > 0) && (
          <section className="card workouts-month-card">
            <h3>
              💪 Treinos
              <span className="workouts-month-label">
                · {(() => {
                  const m = new Date().toLocaleDateString('pt-BR', { month: 'long' });
                  return m.charAt(0).toUpperCase() + m.slice(1);
                })()}
              </span>
              <InfoButton onClick={() => setShowCargaWatchInfo(true)} />
            </h3>

            {/* Total geral do mês — soma de todas as atividades (relógio/pulseira + academia) */}
            {renderWorkoutsGrandTotal(health.workouts_month, health.gym_month_seconds, health.gym_month_calorias, health.gym_month_sessoes)}
          </section>
        )}


        <div className="spacer"></div>
        </>
        )}

        {activeView === 'ia' && (
          <AITab svc={svc} />
        )}

        {activeView === 'agenda' && (
          <AgendaTab svc={svc} />
        )}

        {activeView === 'news' && (
          <NewsTab svc={svc} />
        )}

        {/* Sempre montado (não só quando a aba está ativa) — só escondido via
            CSS. Se ficasse condicionado a activeView como as outras abas, o
            treino em andamento (e o relógio dele) seria destruído ao trocar
            de tela e resetaria tudo ao voltar. */}
        <div style={{ display: activeView === 'workout' ? 'block' : 'none' }}>
          <WorkoutTab svc={svc} />
        </div>

        {activeView === 'nutrition' && (
          <NutritionTab svc={svc} onFoodLogChanged={refreshNutricao} />
        )}

        {activeView === 'status' && (
          <StatusTab svc={svc} />
        )}

        {activeView === 'admin' && authUser?.role === 'admin' && (
          <AdminTab svc={svc} />
        )}

        {activeView === 'activities' && (
          <ActivitiesTab svc={svc} onChanged={refreshActivities} />
        )}

        {activeView === 'saude' && (
          <HealthHourTab svc={svc} />
        )}

        {activeView === 'sono' && (
          <SleepTab svc={svc} />
        )}

        {activeView === 'ecg' && (
          <EcgTab svc={svc} />
        )}

        {activeView === 'composicao' && (
          <BodyCompositionTab svc={svc} />
        )}

        {activeView === 'exames' && (
          <ExamsTab svc={svc} />
        )}

        {activeView === 'estudo' && (
          <StudyPlanTab svc={svc} />
        )}

        {activeView === 'emailfilters' && (
          <PreferencesTab svc={svc} />
        )}

        {activeView === 'onenote' && (
          <div className="onenote-view">
            {/* barra de navegação: voltar + breadcrumb + criar */}
            <div className="one-toolbar">
              {(oneNav.notebook || oneNav.section || oneNav.page) ? (
                <button className="one-back" onClick={oneBack}>‹ Voltar</button>
              ) : (
                <span className="one-crumb-title">📓 Notebooks</span>
              )}
              <div className="one-crumb">
                {oneNav.notebook && <span>{oneNav.notebook.name}</span>}
                {oneNav.section && <span> › {oneNav.section.name}</span>}
                {oneNav.page && <span> › {oneNav.page.title}</span>}
              </div>
              <button className="one-add" onClick={openNoteModal} title="Nova nota">＋</button>
            </div>

            {oneError && <div className="error-banner">{oneError}</div>}
            {oneLoading && <div className="one-loading">⏳ Carregando…</div>}

            {/* Nível 1: notebooks */}
            {!oneNav.notebook && !oneLoading && notebooks && notebooks.map((nb) => (
              <button key={nb.id} className="one-item" onClick={() => openNotebook(nb)}>
                <span className="one-item-icon">📓</span>
                <span className="one-item-label">{nb.name}</span>
                <span className="one-item-arrow">›</span>
              </button>
            ))}

            {/* Nível 2: seções */}
            {oneNav.notebook && !oneNav.section && !oneLoading && sections && sections.map((s) => (
              <button key={s.id} className="one-item" onClick={() => openSection(s)}>
                <span className="one-item-icon">📑</span>
                <span className="one-item-label">{s.name}</span>
                <span className="one-item-arrow">›</span>
              </button>
            ))}

            {/* Nível 3: páginas */}
            {oneNav.section && !oneNav.page && !oneLoading && pages && (
              pages.length === 0
                ? <div className="one-empty">Nenhuma página nesta seção.</div>
                : pages.map((pg) => (
                    <button key={pg.id} className="one-item" onClick={() => openPage(pg)}>
                      <span className="one-item-icon">📄</span>
                      <span className="one-item-label">{pg.title}</span>
                      <span className="one-item-arrow">›</span>
                    </button>
                  ))
            )}

            {/* Nível 4: conteúdo da página */}
            {oneNav.page && !oneLoading && pageHtml !== null && (
              <div className="one-page">
                {oneNav.page.web_url && (
                  <a className="one-open-web" href={oneNav.page.web_url} target="_blank" rel="noopener noreferrer">
                    Abrir no OneNote ↗
                  </a>
                )}
                <div
                  className="one-page-content"
                  dangerouslySetInnerHTML={{ __html: pageHtml }}
                />
              </div>
            )}

            <div className="spacer"></div>
          </div>
        )}
      </div>

      {bcModal && (
        <div className="modal-overlay" onClick={() => setBcModal(null)}>
          <div className="modal bc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{bcModal.icon} {bcModal.label}</h3>
            <div className="bc-modal-list">
              {(() => {
                const serie = (bodyComposition || []).filter((r) => r[bcModal.key] != null);
                if (serie.length === 0) return <p className="bc-modal-empty">Sem medições para este indicador.</p>;
                return serie.map((r, i) => {
                  const v = r[bcModal.key];
                  const anterior = serie[i + 1]?.[bcModal.key];
                  const d = anterior != null ? v - anterior : null;
                  const st = bcModal.status ? bcModal.status(v, r) : null;
                  return (
                    <div className="bc-modal-row" key={r.measured_at}>
                      <span className="bc-modal-date">{fmtMeasuredAt(r.measured_at)}</span>
                      <span className="bc-modal-right">
                        <span className="bc-modal-value">
                          {v}{bcModal.unit && <small>{bcModal.unit}</small>}
                        </span>
                        {d != null && (
                          <span className={`bc-modal-delta ${d > 0 ? 'up' : d < 0 ? 'down' : ''}`}>
                            {d > 0 ? '+' : ''}{Number(d.toFixed(2))}
                          </span>
                        )}
                        {st && <span className={`bc-badge ${st[1]}`}>{st[0]}</span>}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setBcModal(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {noteModal && (
        <div className="modal-overlay" onClick={() => !noteModal.saving && setNoteModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">📝 Nova nota</h3>

            <div className="modal-tabs">
              <button
                className={`modal-tab ${noteModal.mode === 'existing' ? 'active' : ''}`}
                onClick={() => setNoteModal((p) => ({ ...p, mode: 'existing' }))}
              >Seção existente</button>
              <button
                className={`modal-tab ${noteModal.mode === 'new' ? 'active' : ''}`}
                onClick={() => setNoteModal((p) => ({ ...p, mode: 'new' }))}
              >Nova seção</button>
            </div>

            {noteModal.mode === 'existing' ? (
              <select
                className="modal-input"
                value={noteModal.sectionId}
                onChange={(e) => setNoteModal((p) => ({ ...p, sectionId: e.target.value }))}
              >
                <option value="">— escolha a seção —</option>
                {(allSections || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.notebook} › {s.name}</option>
                ))}
              </select>
            ) : (
              <>
                <select
                  className="modal-input"
                  value={noteModal.notebookId}
                  onChange={(e) => setNoteModal((p) => ({ ...p, notebookId: e.target.value }))}
                >
                  {(notebooks || []).map((nb) => (
                    <option key={nb.id} value={nb.id}>{nb.name}</option>
                  ))}
                </select>
                <input
                  className="modal-input"
                  placeholder="Nome da nova seção"
                  value={noteModal.newSection}
                  onChange={(e) => setNoteModal((p) => ({ ...p, newSection: e.target.value }))}
                />
              </>
            )}

            <input
              className="modal-input"
              placeholder="Título da página"
              value={noteModal.title}
              onChange={(e) => setNoteModal((p) => ({ ...p, title: e.target.value }))}
            />
            <textarea
              className="modal-input modal-textarea"
              placeholder="Escreva sua nota…"
              value={noteModal.content}
              onChange={(e) => setNoteModal((p) => ({ ...p, content: e.target.value }))}
            />

            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setNoteModal(null)} disabled={noteModal.saving}>
                Cancelar
              </button>
              <button className="modal-save" onClick={saveNote} disabled={noteModal.saving}>
                {noteModal.saving ? 'Salvando…' : 'Salvar no OneNote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
